import { Box } from "@mui/material";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";
import { DocumentDialogContainer } from "components/Document/shared";
import { ReactNode } from "react";

export interface ExtractionReviewDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /**
   * The document the values were read from. Omit for a caller whose rows did not
   * come from one — the invoice import in 14C reads a spreadsheet — and the
   * dialog falls back to a single full-width review column.
   */
  document?: {
    downloadLink?: string | null;
    fileExtension?: string | null;
  } | null;
  /** True while the document is still being fetched. */
  documentLoading?: boolean;
  /** The review surface: usually an `ExtractionReviewGrid`, plus any banners. */
  children: ReactNode;
}

/**
 * The review surface beside the document it was read from.
 *
 * Checking an extracted value means looking at the page it came from, and a
 * reviewer who has to leave the dialog to do that is being asked to hold the
 * document in their head. The manifest review already works this way; this keeps
 * the layout in one place so every Document-AI ingest gets it rather than each
 * one rediscovering `DocumentDialogContainer` and the two non-obvious things it
 * needs to actually render.
 *
 * The first is a fixed height on the dialog paper: the preview pane sizes itself
 * at `height: 100%`, so without one it has nothing to resolve against and
 * collapses to an empty column.
 *
 * The second is how the review column scrolls. It is a Grid v2 container — flex
 * with wrap — whose computed height is not a reliable scroll target, so setting
 * `overflow` on it directly does not work. Instead the column is
 * `position: relative` and the content sits in an absolutely-positioned box that
 * fills its exact bounds, which is the surface that scrolls. That lets the review
 * column and the document scroll independently, so a long characterization never
 * pushes the page out of view.
 *
 * It renders whatever the caller puts in the review column rather than owning the
 * grid, because a caller has states the grid does not cover — nothing extracted
 * yet, a validation summary, a prompt to start a reading — and those belong
 * beside the document too.
 */
export function ExtractionReviewDialog({
  open,
  onClose,
  title,
  document,
  documentLoading = false,
  children,
}: ExtractionReviewDialogProps) {
  const previewUrl = document?.downloadLink ?? undefined;

  // With nothing to preview none of the split-pane machinery applies, and a
  // fixed-height paper would just leave a tall empty dialog.
  if (!previewUrl) {
    return (
      <DocumentDialogContainer
        open={open}
        onClose={onClose}
        title={title}
        fullWidth
        maxWidth="md"
        showPreviewBoxIfFailed={false}
        formColumnSize={{ xs: 12 }}
        noPrimaryAction
        hideCancelButton
        DialogContentProps={{ sx: { pb: 3 } }}
      >
        {children}
      </DocumentDialogContainer>
    );
  }

  return (
    <DocumentDialogContainer
      open={open}
      onClose={onClose}
      title={title}
      fullWidth
      // Two columns need the room.
      maxWidth="xl"
      sx={{ "& .MuiDialog-paper": { height: "calc(100vh - 80px)", maxHeight: "1100px" } }}
      previewUrl={previewUrl}
      fileExtension={document?.fileExtension ?? undefined}
      previewLoading={documentLoading}
      previewDefaultScale={SpecialZoomLevel.PageWidth}
      formColumnSize={{ xs: 12, md: 6 }}
      previewColumnSize={{ xs: 12, md: 6 }}
      // Pinned so the outer content does not scroll — the two columns do,
      // separately.
      DialogContentProps={{ sx: { overflowY: "hidden" } }}
      formContainerSx={{ position: "relative", overflowY: "visible", pr: 0 }}
      // The review surface carries its own confirm and cancel, so the dialog's
      // action bar is suppressed.
      noPrimaryAction
      hideCancelButton
    >
      <Box sx={{ position: "absolute", inset: 0, overflowY: "auto", pr: 2, pb: 3 }}>{children}</Box>
    </DocumentDialogContainer>
  );
}
